import produce from 'immer';
import { AnyAction } from 'redux';

import { RoleScope } from '../../../typings/enums';
import { createReducer, getErrorStatus, getInitialStatus, getPendingStatus } from '../utils';

import { CLEAR_EDITING_ROLE, CLONE_ROLE, CREATE_ROLE, DELETE_ROLE, FETCH_ALL_ROLES, SET_EDITING_PERMISSIONS, SET_EDITING_ROLE, UPDATE_EDITING_ROLE, UPDATE_ROLE } from './actions';
import { RolesStore } from './index';

const initialState: RolesStore = {
  roles: {},
  status: getInitialStatus(),
  editingRole: {
    id: null,
    name: '',
    description: '',
    scope: RoleScope.Organization,
    permissions: [],
    isCustom: false,
    hasBeenEdited: false,
  },
};

export default createReducer<RolesStore>(initialState, {
  // fetchAll actions
  [`${FETCH_ALL_ROLES}_PENDING`]: (state) => produce(state, draftState => {
    draftState.status = getPendingStatus();
  }),
  [`${FETCH_ALL_ROLES}_FULFILLED`]: (state, action) => produce(state, draftState => {
    draftState.status = getInitialStatus();
    (action.payload.data || []).forEach((role) => {
      if (role?.id) {
        draftState.roles[role.id] = role;
      }
    });
  }),
  [`${FETCH_ALL_ROLES}_REJECTED`]: (state, action) => produce(state, draftState => {
    draftState.status = getErrorStatus(action.payload);
  }),

  // create actions
  [`${CREATE_ROLE}_PENDING`]: (state) => produce(state, draftState => {
    draftState.status = getPendingStatus();
  }),
  [`${CREATE_ROLE}_FULFILLED`]: (state, action) => produce(state, draftState => {
    draftState.status = getInitialStatus();
    draftState.roles[action.payload.data.id] = action.payload.data;
  }),
  [`${CREATE_ROLE}_REJECTED`]: (state, action) => produce(state, draftState => {
    draftState.status = getErrorStatus(action.payload);
  }),

  // update actions
  [`${UPDATE_ROLE}_PENDING`]: (state) => produce(state, draftState => {
    draftState.status = getPendingStatus();
  }),
  [`${UPDATE_ROLE}_FULFILLED`]: (state, action) => produce(state, draftState => {
    draftState.status = getInitialStatus();
    const current = draftState.roles[action.meta.id];
    draftState.roles[action.meta.id] = { ...current, ...action.payload.data };
  }),
  [`${UPDATE_ROLE}_REJECTED`]: (state, action) => produce(state, draftState => {
    draftState.status = getErrorStatus(action.payload);
  }),

  // delete actions
  [`${DELETE_ROLE}_PENDING`]: (state) => produce(state, draftState => {
    draftState.status = getPendingStatus();
  }),
  [`${DELETE_ROLE}_FULFILLED`]: (state, action) => produce(state, draftState => {
    draftState.status = getInitialStatus();
    delete draftState.roles[action.meta.id];
  }),
  [`${DELETE_ROLE}_REJECTED`]: (state, action) => produce(state, draftState => {
    draftState.status = getErrorStatus(action.payload);
  }),
  [SET_EDITING_ROLE]: (state: Readonly<RolesStore>, action: AnyAction) => produce(state, (draftState: RolesStore) => {
    //  routeParams.id will be a string, so unless it's "add" mode we convert it to a number.
    const roleId = action.roleId === 'add'
      ? null
      : Number(action.roleId);
    //  If we have a roleId, it means we "should" be in edit mode and we should have a role in the state to edit.
    if (roleId) {
      const editingRole = Object.values(state.roles).find(role => role.id === roleId) ?? null;
      draftState.editingRole = !editingRole ? initialState.editingRole : {
        id: editingRole.id,
        name: editingRole.name,
        description: editingRole.description,
        scope: editingRole.scope,
        permissions: editingRole.permissions,
        isCustom: editingRole.isCustom,
        hasBeenEdited: false,
      };
    } else {
      //  Reset, for example if the add/edit roles page unmounts.
      draftState.editingRole = {
        ...initialState.editingRole,
        id: 'add',
        hasBeenEdited: false,
      };
    }
  }),
  [SET_EDITING_PERMISSIONS]: (
    state: Readonly<RolesStore>,
    { permissions },
  ) => produce(state, (draftState: RolesStore) => {
    draftState.editingRole = {
      ...state.editingRole,
      permissions,
      hasBeenEdited: false,
    };
  }),
  [UPDATE_EDITING_ROLE]: (
    state: Readonly<RolesStore>,
    { updatedEditingRole }: AnyAction,
  ) => produce(state, (draftState: RolesStore) => {
    const isScopeChange = (updatedEditingRole?.scope && updatedEditingRole.scope !== state.editingRole.scope);
    draftState.editingRole = {
      ...state.editingRole,
      ...updatedEditingRole,
      hasBeenEdited: true,
    };
    if (isScopeChange) {
      draftState.editingRole.permissions = draftState.editingRole.permissions
        .filter(permission => permission.scopes.includes(draftState.editingRole.scope));
    }
  }),
  //  Works almost the same as updateEditingRole, but without checking for scope change/unchecking out-of-scope permissions.
  [CLONE_ROLE]: (state: Readonly<RolesStore>, { clonedRole }: AnyAction) => produce(state, (draftState: RolesStore) => {
    draftState.editingRole = {
      ...state.editingRole,
      ...clonedRole,
      hasBeenEdited: false,
    };
  }),
  [CLEAR_EDITING_ROLE]: (state: Readonly<RolesStore>) => produce(state, (draftState: RolesStore) => {
    draftState.editingRole = {
      ...initialState.editingRole,
    };
  }),
});
